import { Fragment } from 'react';

import ChatVisualization from './ChatVisualization';
import {
  ChatBlock,
  hasPendingVisualization,
  parseChatMessage,
} from './parseChatMessage';

type ChatMessageRendererProps = {
  content: string;
  variant?: 'user' | 'assistant';
  isStreaming?: boolean;
};

const EMAIL_PATTERN = '[A-Z0-9._%+-]+@[A-Z0-9.-]+\\.[A-Z]{2,}';
const INLINE_MARKDOWN_PATTERN = new RegExp(
  `(\\*\\*[^*]+\\*\\*|\\*[^*]+\\*|\\[[^\\]]+\\]\\([^)]+\\)|mailto:${EMAIL_PATTERN}|https?:\\/\\/[^\\s<>()]+|www\\.[^\\s<>()]+|${EMAIL_PATTERN})`,
  'gi',
);
const AUTO_LINK_PATTERN = new RegExp(
  `^(mailto:${EMAIL_PATTERN}|https?:\\/\\/[^\\s<>()]+|www\\.[^\\s<>()]+|${EMAIL_PATTERN})$`,
  'i',
);
const BOLD_PATTERN = /^\*\*([^*]+)\*\*$/;
const ITALIC_PATTERN = /^\*([^*]+)\*$/;
const LINK_PATTERN = /^\[([\s\S]+?)\]\((.+?)\)$/;

const isSafeHref = (href: string): boolean => {
  try {
    const url = new URL(href);
    return (
      url.protocol === 'http:' ||
      url.protocol === 'https:' ||
      url.protocol === 'mailto:'
    );
  } catch {
    return false;
  }
};

const linkClassName = (variant: 'user' | 'assistant') =>
  variant === 'user'
    ? 'font-medium text-white underline underline-offset-2 hover:text-white/90'
    : 'font-medium text-indigo-600 underline underline-offset-2 hover:text-indigo-700';

const renderInlineFormatting = (
  content: string,
  variant: 'user' | 'assistant',
) => {
  const parts = content
    .split(INLINE_MARKDOWN_PATTERN)
    .filter((part) => part.length > 0);

  return parts.map((part, index) => {
    const boldMatch = part.match(BOLD_PATTERN);
    if (boldMatch) {
      return (
        <strong
          key={`${index}-${boldMatch[1]}`}
          className={
            variant === 'user'
              ? 'font-semibold text-white'
              : 'text-black-900 font-semibold'
          }
        >
          {renderInlineFormatting(boldMatch[1], variant)}
        </strong>
      );
    }
    const italicMatch = part.match(ITALIC_PATTERN);
    if (italicMatch) {
      return (
        <em
          key={`${index}-${italicMatch[1]}`}
          className={
            variant === 'user'
              ? 'text-white/90 italic'
              : 'text-black-700 italic'
          }
        >
          {renderInlineFormatting(italicMatch[1], variant)}
        </em>
      );
    }

    const autoLinkMatch = part.match(AUTO_LINK_PATTERN);
    if (autoLinkMatch) {
      const cleaned = part.trim().replace(/[.,;:!?]+$/, '');

      const isMailto = cleaned.toLowerCase().startsWith('mailto:');
      const isPlainEmail =
        !isMailto &&
        !cleaned.toLowerCase().startsWith('http://') &&
        !cleaned.toLowerCase().startsWith('https://') &&
        !cleaned.toLowerCase().startsWith('www.');

      const href = isMailto
        ? cleaned
        : isPlainEmail
          ? `mailto:${cleaned}`
          : cleaned.toLowerCase().startsWith('www.')
            ? `https://${cleaned}`
            : cleaned;

      if (!isSafeHref(href)) {
        return <Fragment key={`${index}-${part}`}>{part}</Fragment>;
      }

      const isEmailLink = href.toLowerCase().startsWith('mailto:');

      const displayText = isEmailLink ? href.replace(/^mailto:/i, '') : cleaned;

      return (
        <a
          key={`${index}-${href}`}
          href={href}
          target={isEmailLink ? undefined : '_blank'}
          rel={isEmailLink ? undefined : 'noopener noreferrer'}
          className={linkClassName(variant)}
        >
          {displayText}
        </a>
      );
    }

    const linkMatch = part.match(LINK_PATTERN);
    if (linkMatch) {
      const [, rawLabel, rawHref] = linkMatch;

      const href = rawHref.trim();
      const label = rawLabel.trim();

      if (!isSafeHref(href)) {
        return <Fragment key={`${index}-${part}`}>{part}</Fragment>;
      }

      const isEmailLink = href.toLowerCase().startsWith('mailto:');

      const displayLabel = isEmailLink
        ? label.replace(/^mailto:/i, '').trim()
        : label;

      return (
        <a
          key={`${index}-${href}`}
          href={href}
          target={isEmailLink ? undefined : '_blank'}
          rel={isEmailLink ? undefined : 'noopener noreferrer'}
          className={linkClassName(variant)}
        >
          {displayLabel}
        </a>
      );
    }

    return <Fragment key={`${index}-${part}`}>{part}</Fragment>;
  });
};

const renderBlock = (
  block: ChatBlock,
  variant: 'user' | 'assistant',
  index: number,
) => {
  switch (block.type) {
    case 'heading': {
      const headingClass =
        block.level <= 3
          ? 'mt-3 mb-1 text-sm font-semibold text-black-900'
          : 'mt-2 mb-1 text-xs font-semibold text-black-800';

      return (
        <p key={`heading-${index}`} className={headingClass}>
          {renderInlineFormatting(block.content, variant)}
        </p>
      );
    }

    case 'list':
      return (
        <ul key={`list-${index}`} className="mt-2 list-disc space-y-1 pl-5">
          {block.items.map((item, itemIndex) => (
            <li key={`list-item-${index}-${itemIndex}`}>
              {renderInlineFormatting(item, variant)}
            </li>
          ))}
        </ul>
      );

    case 'visualization':
      return (
        <ChatVisualization
          key={`visualization-${index}`}
          visualization={block.visualization}
        />
      );

    case 'paragraph':
    default:
      return (
        <p
          key={`paragraph-${index}`}
          className="mt-2 leading-relaxed first:mt-0"
        >
          {renderInlineFormatting(block.content, variant)}
        </p>
      );
  }
};

export default function ChatMessageRenderer({
  content,
  variant = 'assistant',
  isStreaming = false,
}: ChatMessageRendererProps) {
  const blocks = parseChatMessage(content);
  const showPendingVisualizationLoader =
    isStreaming && hasPendingVisualization(content);
  const hasRenderedVisualization = blocks.some(
    (block) => block.type === 'visualization',
  );

  if (blocks.length === 0 && !showPendingVisualizationLoader) {
    return null;
  }

  return (
    <div className="space-y-1">
      {blocks.map((block, index) => renderBlock(block, variant, index))}
      {showPendingVisualizationLoader && !hasRenderedVisualization && (
        <ChatVisualization isLoading />
      )}
    </div>
  );
}
